import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LanguageDetector from './components/LanguageDetector';
import SummerOfferBanner from './components/SummerOfferBanner';
import Header from './components/Header';
import VerticalNav from './components/VerticalNav';
import Testimonials from './components/Testimonials';
import GoogleMapsSeoArticle from './components/GoogleMapsSeoArticle';
import WebdesignArticle from './components/WebdesignArticle';
import Footer from './components/Footer';
import { FormProvider } from './components/FormManager';
import ModalContext from './context/ModalContext';
import ContactModal from './components/ContactModal';
import FormPopup from './components/FormPopup';
import HomePage from './pages/HomePage';
import About from './components/About';
import ServicesPage from './components/ServicesPage';
import PricingPage from './components/PricingPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import DankePage from './pages/DankePage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import EditorPage from './pages/EditorPage';
import WordCountPage from './pages/WordCountPage';
import BlogPostSalonBayern from './components/blog/BlogPost_SalonBayern';
import BlogPostHandwerkerSEO from './components/blog/BlogPost_HandwerkerSEO';
import BlogPostOnlineMarketingStrategien from './components/blog/BlogPostOnlineMarketingStrategien';
import DigitalMarketingArticle from './components/DigitalMarketingArticle';
import SeoMasterclassArticle from './components/SeoMasterclassArticle';
import BookingSystemArticle from './components/BookingSystemArticle';
import ReputationManagementArticle from './components/ReputationManagementArticle';
import EmailMarketingArticle from './components/EmailMarketingArticle';
import PerformanceMarketingAgenturArticle from './components/PerformanceMarketingAgenturArticle';
import WerbeagenturArticle from './components/WerbeagenturArticle';
import FullServiceAgenturArticle from './components/FullServiceAgenturArticle';
import OnlineMarketingAgenturArticle from './components/OnlineMarketingAgenturArticle';
import DigitalagenturArticle from './components/DigitalagenturArticle';
import SeoAgenturArticle from './components/SeoAgenturArticle';
import LocalSeoAgenturArticle from './components/LocalSeoAgenturArticle';
import GoogleAdsAgenturArticle from './components/GoogleAdsAgenturArticle';
import KiAgenturArticle from './components/KiAgenturArticle';
import PaidAdvertisingArticle from './components/PaidAdvertisingArticle';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

// Main layout component that includes all the global UI elements
const MainLayout = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <div className="page-container">
      <LanguageDetector />
      <SummerOfferBanner />
      <Header />
      {isHomePage && <VerticalNav />}
      <main className="main-content">
        <Outlet />
      </main>
      <Footer />
      <FormPopup formType="general" />
      <FormPopup formType="summer" />
    </div>
  );
};

function App() {
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Site-wide structured data
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Enzlo',
    'legalName': 'Spoddig UG (haftungsbeschränkt)',
    'url': 'https://enzlo.com',
    'logo': 'https://enzlo.com/assets/logo.png',
    'sameAs': [
      'https://www.facebook.com/enzloinc/',
      'https://www.youtube.com/@Totalemailautoresponder'
    ]
  };

  const webSiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'url': 'https://enzlo.com',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': 'https://enzlo.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webSiteJsonLd)}</script>
      </Helmet>
      <Router>
        <ScrollToTop />
        <ModalContext.Provider value={{ openModal }}>
          <ContactModal isOpen={isModalOpen} onClose={closeModal} />
          <FormProvider>
            <Routes>
              {/* Main app routes with layout */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/google-maps-seo-optimieren" element={<GoogleMapsSeoArticle />} />
                <Route path="/website-checkliste-fuer-salons-und-handwerker" element={<WebdesignArticle />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/hochkonvertierende-website-schoenheitssalon-bayern" element={<BlogPostSalonBayern />} />
                <Route path="/blog/lokale-seo-handwerker-sueddeutschland" element={<BlogPostHandwerkerSEO />} />
                <Route path="/blog/online-marketing-strategien-kleine-unternehmen" element={<BlogPostOnlineMarketingStrategien />} />
                <Route path="/blog/digitales-marketing-fuer-salons-und-handwerker" element={<DigitalMarketingArticle />} />
                <Route path="/blog/seo-masterclass-fuer-salons-und-handwerker" element={<SeoMasterclassArticle />} />
                <Route path="/blog/online-buchungssystem-masterclass" element={<BookingSystemArticle />} />
                <Route path="/blog/reputationsmanagement-masterclass" element={<ReputationManagementArticle />} />
                <Route path="/email-marketing-masterclass" element={<EmailMarketingArticle />} />
                <Route path="/performance-marketing-agentur" element={<PerformanceMarketingAgenturArticle />} />
                <Route path="/werbeagentur" element={<WerbeagenturArticle />} />
                <Route path="/full-service-agentur" element={<FullServiceAgenturArticle />} />
                <Route path="/online-marketing-agentur" element={<OnlineMarketingAgenturArticle />} />
                <Route path="/digitalagentur" element={<DigitalagenturArticle />} />
                <Route path="/seo-agentur" element={<SeoAgenturArticle />} />
                <Route path="/local-seo-agentur" element={<LocalSeoAgenturArticle />} />
                <Route path="/google-ads-agentur" element={<GoogleAdsAgenturArticle />} />
                <Route path="/ki-agentur" element={<KiAgenturArticle />} />
                <Route path="/google-meta-ads-fuer-salons-und-handwerker" element={<PaidAdvertisingArticle />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/word-count" element={<WordCountPage />} />
                <Route path="/danke" element={<DankePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Standalone routes without layout */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/editor" element={<EditorPage />} />
            </Routes>
          </FormProvider>
        </ModalContext.Provider>
      </Router>
      <CookieConsent />
    </>
  );
}

export default App;
